import { ApiFilterOperation } from '@eas/common-web';
import type { TableFilter, TableFilterState } from '@eas/common-web';
import type { WorkOrder } from '@models';
import {
  normalizeBusinessWorkOrderIds,
  normalizeWorkOrderInternalIds,
} from './work-order-id-utils';

export type ShowSelectedWorkOrdersTableFilterSyncSignal = {
  key: number;
  ids: Array<NonNullable<WorkOrder['id']>>;
  workOrderIds: Array<NonNullable<WorkOrder['workOrderId']>>;
};

type ApplyShowSelectedWorkOrdersTableFilterSyncParams = {
  filters: TableFilter[];
  filtersState: TableFilterState[];
  signal?: ShowSelectedWorkOrdersTableFilterSyncSignal;
};

type ConsumeShowSelectedWorkOrdersTableFilterSyncParams = {
  filters: TableFilter[];
  prevFiltersState: TableFilterState[];
  signal?: ShowSelectedWorkOrdersTableFilterSyncSignal;
  lastAppliedSignalKey?: number;
};

let signalKeySequence = 0;

const resetFilterState = (state: TableFilterState): TableFilterState => ({
  ...state,
  value: null,
  enabled: false,
  object: null,
  filters: [],
  selectFilter: [],
  numberFilter: null,
});

export function createShowSelectedWorkOrdersTableFilterSyncSignal({
  ids,
  workOrderIds,
}: {
  ids: Array<WorkOrder['id']>;
  workOrderIds: Array<WorkOrder['workOrderId']>;
}): ShowSelectedWorkOrdersTableFilterSyncSignal {
  signalKeySequence += 1;

  return {
    key: signalKeySequence,
    ids: normalizeWorkOrderInternalIds(ids),
    workOrderIds: normalizeBusinessWorkOrderIds(workOrderIds),
  };
}

export function applyShowSelectedWorkOrdersTableFilterSync({
  filters,
  filtersState,
  signal,
}: ApplyShowSelectedWorkOrdersTableFilterSyncParams) {
  if (!signal || signal.workOrderIds.length === 0 || signal.ids.length === 0) {
    return {
      shouldApply: false,
      nextFiltersState: filtersState,
    };
  }

  const workOrderIdIndex = filters.findIndex(
    (filter) => filter.filterkey === 'workOrderId'
  );

  if (workOrderIdIndex === -1 || !filtersState[workOrderIdIndex]) {
    return {
      shouldApply: false,
      nextFiltersState: filtersState,
    };
  }

  const nextFiltersState = filtersState.map(resetFilterState);

  nextFiltersState[workOrderIdIndex] = {
    ...nextFiltersState[workOrderIdIndex],
    enabled: true,
    operation: ApiFilterOperation.AND,
    value: signal.workOrderIds.join(' '),
    filters: [
      {
        operation: ApiFilterOperation.IN,
        field: 'workOrderId',
        values: signal.workOrderIds,
      },
    ],
  };

  return {
    shouldApply: true,
    nextFiltersState,
  };
}

export function consumeShowSelectedWorkOrdersTableFilterSync({
  filters,
  prevFiltersState,
  signal,
  lastAppliedSignalKey,
}: ConsumeShowSelectedWorkOrdersTableFilterSyncParams) {
  if (!signal || lastAppliedSignalKey === signal.key) {
    return {
      shouldApply: false,
      shouldClearSaved: false,
      calendarFilterIds: undefined,
      nextAppliedSignalKey: lastAppliedSignalKey,
      nextFiltersState: prevFiltersState,
    };
  }

  const result = applyShowSelectedWorkOrdersTableFilterSync({
    filters,
    filtersState: prevFiltersState,
    signal,
  });

  if (!result.shouldApply) {
    return {
      shouldApply: false,
      shouldClearSaved: false,
      calendarFilterIds: undefined,
      nextAppliedSignalKey: lastAppliedSignalKey,
      nextFiltersState: prevFiltersState,
    };
  }

  return {
    shouldApply: true,
    shouldClearSaved: true,
    calendarFilterIds: signal.ids,
    nextAppliedSignalKey: signal.key,
    nextFiltersState: result.nextFiltersState,
  };
}
